import type { SimConfig, SolidRect } from './config';
import { TICK_SECONDS } from './constants';
import { detCos, detSin } from './detmath';
import type { EventSink } from './events';
import { nearestFreeCell } from './geometry';
import { nextDiagonalDirection, nextFloat, type RngHolder } from './rng';
import type { GoldenBallState, InputVector, MatchState, PlayerBallState } from './types';

/** Maximum distance a body may travel in one substep (well below the smallest radius). */
const MAX_SUBSTEP_TRAVEL = 0.1;
const MAX_SUBSTEPS = 8;
const IMPACT_EVENT_THRESHOLD = 0.3;
/** Keep pairs slightly apart after a hit so the next substep cannot immediately re-glue them. */
const CONTACT_SLOP = 0.012;
/**
 * Slow rear-end taps otherwise leave the player glued to the bomb (mass 3 vs 1, e = 1).
 * Fast hits already separate harder than this, so they are unchanged.
 */
const PLAYER_GOLDEN_MIN_SEPARATION = 2.4;
/** When a pair is packed between solids, slide them out of the gap along the tangent. */
const CORRIDOR_SLIDE = 3.6;
/**
 * A player who meets a surface slower than this slides along it.
 * Faster hits still rebound. Golden balls always rebound (0).
 */
const PLAYER_REST_SPEED = 1.5;

function solidPenetration(config: SimConfig, x: number, y: number, r: number): number {
  let max = 0;
  if (x - r < 0) max = Math.max(max, r - x);
  if (x + r > config.columns) max = Math.max(max, x + r - config.columns);
  if (y - r < 0) max = Math.max(max, r - y);
  if (y + r > config.rows) max = Math.max(max, y + r - config.rows);
  for (const rect of config.solids) {
    const cx = Math.max(rect.x0, Math.min(x, rect.x1));
    const cy = Math.max(rect.y0, Math.min(y, rect.y1));
    const dx = x - cx;
    const dy = y - cy;
    const d2 = dx * dx + dy * dy;
    if (d2 >= r * r) continue;
    max = Math.max(max, d2 > 0 ? r - Math.sqrt(d2) : r);
  }
  return max;
}

interface Body {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface Material {
  restitution: number;
  friction: number;
}

export function normalizeInput(input: InputVector | undefined): InputVector {
  let x = input && Number.isFinite(input.x) ? input.x : 0;
  let y = input && Number.isFinite(input.y) ? input.y : 0;
  x = Math.max(-1, Math.min(1, x));
  y = Math.max(-1, Math.min(1, y));
  const lengthSq = x * x + y * y;
  if (lengthSq > 1) {
    const length = Math.sqrt(lengthSq);
    x /= length;
    y /= length;
  }
  return { x: x === 0 ? 0 : x, y: y === 0 ? 0 : y };
}

function speedOf(b: Body): number {
  return Math.sqrt(b.vx * b.vx + b.vy * b.vy);
}

function capSpeed(b: Body, cap: number): void {
  const speed = speedOf(b);
  if (speed > cap) {
    const k = cap / speed;
    b.vx *= k;
    b.vy *= k;
  }
}

/**
 * Input -> acceleration -> velocity. Input never sets velocity directly.
 * Input can only add speed up to `maxInputSpeed`; above it (after an impulse) input may
 * steer but not accelerate further. Damping then applies every tick.
 */
export function integratePlayerVelocity(p: PlayerBallState, input: InputVector, config: SimConfig) {
  const c = config.player;
  const before = speedOf(p);
  let vx = p.vx + input.x * c.accelerationPerTick;
  let vy = p.vy + input.y * c.accelerationPerTick;
  const after = Math.sqrt(vx * vx + vy * vy);
  if (after > c.maxInputSpeed && after > before) {
    const limit = Math.max(c.maxInputSpeed, before);
    vx *= limit / after;
    vy *= limit / after;
  }
  p.vx = vx * c.dampingFactor;
  p.vy = vy * c.dampingFactor;
}

/** Golden balls drift back towards their cruising speed. */
export function regulateGoldenSpeed(g: GoldenBallState, config: SimConfig, rng: RngHolder) {
  const c = config.golden;
  const speed = speedOf(g);
  if (speed < 1e-9) {
    const dir = nextDiagonalDirection(rng, c.minAxisAngleDegrees);
    const start = c.cruiseSpeed * c.underspeedLerp;
    g.vx = dir.x * start;
    g.vy = dir.y * start;
    return;
  }
  const target =
    speed > c.cruiseSpeed
      ? c.cruiseSpeed + (speed - c.cruiseSpeed) * c.overspeedFactor
      : speed + (c.cruiseSpeed - speed) * c.underspeedLerp;
  g.vx *= target / speed;
  g.vy *= target / speed;
}

/**
 * `normalVel` points into the surface when negative on the left/bottom branches
 * and positive on the right/top branches: the caller always passes the raw axis
 * component, and a rebound negates it. A soft contact drops that component so the
 * body slides instead of buzzing.
 */
function surfaceResponse(
  normalVel: number,
  tangentVel: number,
  m: Material,
  restSpeed: number,
): readonly [number, number] {
  if (restSpeed > 0 && Math.abs(normalVel) < restSpeed) return [0, tangentVel];
  return [-normalVel * m.restitution, tangentVel * (1 - m.friction)];
}

/** Resolves a circle against the arena boundary. Returns the impact speed (0 if none). */
function collideBounds(
  b: Body,
  r: number,
  config: SimConfig,
  m: Material,
  restSpeed: number,
): number {
  let impact = 0;
  const apply = (normalVel: number, tangentVel: number) => {
    const [normal, tangent] = surfaceResponse(normalVel, tangentVel, m, restSpeed);
    if (normal !== 0) impact = Math.max(impact, Math.abs(normalVel));
    return [normal, tangent] as const;
  };
  if (b.x - r < 0) {
    b.x = r;
    if (b.vx < 0) [b.vx, b.vy] = apply(b.vx, b.vy);
  } else if (b.x + r > config.columns) {
    b.x = config.columns - r;
    if (b.vx > 0) [b.vx, b.vy] = apply(b.vx, b.vy);
  }
  if (b.y - r < 0) {
    b.y = r;
    if (b.vy < 0) [b.vy, b.vx] = apply(b.vy, b.vx);
  } else if (b.y + r > config.rows) {
    b.y = config.rows - r;
    if (b.vy > 0) [b.vy, b.vx] = apply(b.vy, b.vx);
  }
  return impact;
}

/** Circle vs axis-aligned rectangle: closest point, normal, depenetration, impulse, friction. */
function collideRect(b: Body, r: number, rect: SolidRect, m: Material, restSpeed: number): number {
  const cx = Math.max(rect.x0, Math.min(b.x, rect.x1));
  const cy = Math.max(rect.y0, Math.min(b.y, rect.y1));
  const dx = b.x - cx;
  const dy = b.y - cy;
  const d2 = dx * dx + dy * dy;
  if (d2 >= r * r) return 0;
  let nx: number;
  let ny: number;
  let penetration: number;
  if (d2 > 0) {
    const d = Math.sqrt(d2);
    nx = dx / d;
    ny = dy / d;
    penetration = r - d;
  } else {
    // Centre inside the rectangle: push out along the shallowest side.
    // Deterministic tie order: left, right, top, bottom.
    const left = b.x - rect.x0;
    const right = rect.x1 - b.x;
    const top = b.y - rect.y0;
    const bottom = rect.y1 - b.y;
    const min = Math.min(left, right, top, bottom);
    if (min === left) [nx, ny] = [-1, 0];
    else if (min === right) [nx, ny] = [1, 0];
    else if (min === top) [nx, ny] = [0, -1];
    else [nx, ny] = [0, 1];
    penetration = min + r;
  }
  b.x += nx * penetration;
  b.y += ny * penetration;
  const vn = b.vx * nx + b.vy * ny;
  if (vn >= 0) return 0;
  const tx = b.vx - vn * nx;
  const ty = b.vy - vn * ny;
  if (restSpeed > 0 && -vn < restSpeed) {
    b.vx = tx;
    b.vy = ty;
    return 0;
  }
  const vnAfter = -vn * m.restitution;
  const keep = 1 - m.friction;
  b.vx = vnAfter * nx + tx * keep;
  b.vy = vnAfter * ny + ty * keep;
  return -vn;
}

function collideStatic(
  b: Body,
  r: number,
  config: SimConfig,
  m: Material,
  restSpeed: number,
  onImpact: ((surface: string, strength: number) => void) | null,
): number {
  let impact = collideBounds(b, r, config, m, restSpeed);
  if (impact > 0 && onImpact) onImpact('wall', impact);
  for (const rect of config.solids) {
    if (b.x + r <= rect.x0 || b.x - r >= rect.x1 || b.y + r <= rect.y0 || b.y - r >= rect.y1) {
      continue;
    }
    const hit = collideRect(b, r, rect, m, restSpeed);
    if (hit > 0) {
      impact = Math.max(impact, hit);
      if (onImpact) onImpact(rect.id, hit);
    }
  }
  return impact;
}

/**
 * Circle vs circle. If a proposed separation would drive a body deeper into a wall/block,
 * that body is treated as blocked (infinite mass for this contact) so the other body pops
 * off instead of the pair remaining glued in a corridor.
 * Returns -1 if not touching, else the pre-impulse closing speed (0 if only a positional fix).
 */
function collideCircles(
  a: Body,
  ra: number,
  ia: number,
  b: Body,
  rb: number,
  ib: number,
  restitution: number,
  config: SimConfig,
  minSeparatingSpeed = 0,
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d2 = dx * dx + dy * dy;
  const rs = ra + rb;
  if (d2 >= rs * rs) return -1;
  let nx = 0;
  let ny = 1;
  let d = 0;
  if (d2 > 0) {
    d = Math.sqrt(d2);
    nx = dx / d;
    ny = dy / d;
  }
  const penetration = rs + CONTACT_SLOP - d;
  let iaEff = ia;
  let ibEff = ib;
  const probe = Math.max(penetration, 0.05);
  const aNow = solidPenetration(config, a.x, a.y, ra);
  const bNow = solidPenetration(config, b.x, b.y, rb);
  const aBlocked = solidPenetration(config, a.x - nx * probe, a.y - ny * probe, ra) > aNow + 1e-6;
  const bBlocked = solidPenetration(config, b.x + nx * probe, b.y + ny * probe, rb) > bNow + 1e-6;
  if (aBlocked || bBlocked) {
    const tx = -ny;
    const ty = nx;
    const escape = 0.7;
    const plus =
      solidPenetration(config, a.x + tx * escape, a.y + ty * escape, ra) +
      solidPenetration(config, b.x + tx * escape, b.y + ty * escape, rb);
    const minus =
      solidPenetration(config, a.x - tx * escape, a.y - ty * escape, ra) +
      solidPenetration(config, b.x - tx * escape, b.y - ty * escape, rb);
    const dir = plus <= minus ? 1 : -1;
    const relT = (a.vx * tx + a.vy * ty) * dir;
    if (relT < CORRIDOR_SLIDE) {
      const boost = CORRIDOR_SLIDE - relT;
      a.vx += tx * dir * boost;
      a.vy += ty * dir * boost;
      b.vx += tx * dir * boost;
      b.vy += ty * dir * boost;
    }
    const shift = Math.min(Math.max(penetration, 0.03), 0.1);
    a.x += tx * dir * shift;
    a.y += ty * dir * shift;
    b.x += tx * dir * shift;
    b.y += ty * dir * shift;
    if (aBlocked && bBlocked) return 0;
  }
  if (bBlocked) ibEff = 0;
  else if (aBlocked) iaEff = 0;
  const inverseSum = iaEff + ibEff;
  if (inverseSum === 0) return 0;
  const ka = iaEff / inverseSum;
  const kb = ibEff / inverseSum;
  a.x -= nx * penetration * ka;
  a.y -= ny * penetration * ka;
  b.x += nx * penetration * kb;
  b.y += ny * penetration * kb;
  const relativeNormal = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
  const bounce = relativeNormal < 0 ? -restitution * relativeNormal : relativeNormal;
  const target = Math.max(bounce, minSeparatingSpeed);
  if (target <= relativeNormal) return 0;
  const j = (target - relativeNormal) / inverseSum;
  a.vx -= j * iaEff * nx;
  a.vy -= j * iaEff * ny;
  b.vx += j * ibEff * nx;
  b.vy += j * ibEff * ny;
  return relativeNormal < 0 ? -relativeNormal : 0;
}

function jitterVelocity(g: Body, config: SimConfig, rng: RngHolder) {
  const max = config.golden.reboundJitterRadians;
  if (max <= 0) return;
  const angle = (nextFloat(rng) * 2 - 1) * max;
  const c = detCos(angle);
  const s = detSin(angle);
  const vx = g.vx * c - g.vy * s;
  const vy = g.vx * s + g.vy * c;
  g.vx = vx;
  g.vy = vy;
}

function insideSolid(config: SimConfig, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x > config.columns || y > config.rows) return true;
  for (const rect of config.solids) {
    if (x > rect.x0 && x < rect.x1 && y > rect.y0 && y < rect.y1) return true;
  }
  return false;
}

export interface PhysicsOptions {
  /** Players that cannot move this tick (countdown or stunned): infinite mass, zero velocity. */
  readonly frozen: readonly [boolean, boolean];
  readonly inputs: readonly [InputVector, InputVector];
}

/**
 * Advances all tangible bodies by one tick. Fills `contacts[p * goldenCount + g]` with 1
 * when player `p` touched golden ball `g` at any substep.
 */
export function stepPhysics(
  state: MatchState,
  config: SimConfig,
  options: PhysicsOptions,
  contacts: Uint8Array,
  sink: EventSink,
): void {
  const players = state.players;
  const goldens = state.goldens;
  const pr = config.player.radius;
  const gr = config.golden.radius;
  const playerMaterial: Material = {
    restitution: config.player.wallRestitution,
    friction: config.player.tangentialFriction,
  };
  const goldenMaterial: Material = { restitution: config.golden.restitution, friction: 0 };

  for (let i = 0; i < 2; i++) {
    const p = players[i] as PlayerBallState;
    if (options.frozen[i]) {
      p.vx = 0;
      p.vy = 0;
    } else {
      integratePlayerVelocity(p, options.inputs[i] as InputVector, config);
    }
  }
  const tangible: boolean[] = goldens.map((g) => g.status === 'NEUTRAL' || g.status === 'ARMED');
  goldens.forEach((g, i) => {
    if (tangible[i]) regulateGoldenSpeed(g, config, state);
  });

  let maxSpeed = 0;
  for (const p of players) maxSpeed = Math.max(maxSpeed, speedOf(p));
  goldens.forEach((g, i) => {
    if (tangible[i]) maxSpeed = Math.max(maxSpeed, speedOf(g));
  });
  const substeps = Math.min(
    MAX_SUBSTEPS,
    Math.max(1, Math.ceil((maxSpeed * TICK_SECONDS) / MAX_SUBSTEP_TRAVEL)),
  );
  const dt = TICK_SECONDS / substeps;
  const playerInverse = [
    options.frozen[0] ? 0 : config.player.inverseMass,
    options.frozen[1] ? 0 : config.player.inverseMass,
  ] as const;

  const impacts = new Map<string, number>();
  const recordImpact = (a: string, b: string, strength: number) => {
    if (strength < IMPACT_EVENT_THRESHOLD) return;
    const key = `${a}|${b}`;
    impacts.set(key, Math.max(impacts.get(key) ?? 0, strength));
  };

  for (let s = 0; s < substeps; s++) {
    for (let i = 0; i < 2; i++) {
      if (options.frozen[i]) continue;
      const p = players[i] as PlayerBallState;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      collideStatic(p, pr, config, playerMaterial, PLAYER_REST_SPEED, (surface, strength) =>
        recordImpact(p.id, surface, strength),
      );
    }
    goldens.forEach((g, i) => {
      if (!tangible[i]) return;
      g.x += g.vx * dt;
      g.y += g.vy * dt;
      if (collideStatic(g, gr, config, goldenMaterial, 0, null) > 0)
        jitterVelocity(g, config, state);
    });

    const [p0, p1] = players;
    const pp = collideCircles(
      p0,
      pr,
      playerInverse[0],
      p1,
      pr,
      playerInverse[1],
      config.player.playerRestitution,
      config,
    );
    if (pp > 0) recordImpact(p0.id, p1.id, pp);

    const resolvePlayerGoldens = () => {
      for (let i = 0; i < 2; i++) {
        const p = players[i] as PlayerBallState;
        goldens.forEach((g, gi) => {
          if (!tangible[gi]) return;
          const hit = collideCircles(
            p,
            pr,
            playerInverse[i] as number,
            g,
            gr,
            config.golden.inverseMass,
            config.golden.playerContactRestitution,
            config,
            PLAYER_GOLDEN_MIN_SEPARATION,
          );
          if (hit >= 0) {
            contacts[i * goldens.length + gi] = 1;
            if (hit > 0) recordImpact(p.id, g.id, hit);
          }
        });
      }
    };
    resolvePlayerGoldens();

    for (let a = 0; a < goldens.length; a++) {
      if (!tangible[a]) continue;
      for (let b = a + 1; b < goldens.length; b++) {
        if (!tangible[b]) continue;
        collideCircles(
          goldens[a] as GoldenBallState,
          gr,
          config.golden.inverseMass,
          goldens[b] as GoldenBallState,
          gr,
          config.golden.inverseMass,
          config.golden.restitution,
          config,
        );
      }
    }

    // Circle impulses can push a body into a wall/block; repair before the next substep.
    for (let i = 0; i < 2; i++) {
      if (options.frozen[i]) continue;
      collideStatic(
        players[i] as PlayerBallState,
        pr,
        config,
        playerMaterial,
        PLAYER_REST_SPEED,
        null,
      );
    }
    goldens.forEach((g, i) => {
      if (!tangible[i]) return;
      collideStatic(g, gr, config, goldenMaterial, 0, null);
    });
    // Static repair can squeeze a pair back together in a 1-cell corridor; pop them apart.
    resolvePlayerGoldens();
    for (let i = 0; i < 2; i++) {
      if (options.frozen[i]) continue;
      collideStatic(
        players[i] as PlayerBallState,
        pr,
        config,
        playerMaterial,
        PLAYER_REST_SPEED,
        null,
      );
    }
    goldens.forEach((g, i) => {
      if (!tangible[i]) return;
      collideStatic(g, gr, config, goldenMaterial, 0, null);
    });
  }

  // Extra static passes: mixed wall/block corners can need more than one depenetration.
  for (let pass = 0; pass < 3; pass++) {
    for (let i = 0; i < 2; i++) {
      if (options.frozen[i]) continue;
      collideStatic(
        players[i] as PlayerBallState,
        pr,
        config,
        playerMaterial,
        PLAYER_REST_SPEED,
        null,
      );
    }
    goldens.forEach((g, i) => {
      if (!tangible[i]) return;
      collideStatic(g, gr, config, goldenMaterial, 0, null);
    });
  }
  for (let i = 0; i < 2; i++) {
    if (options.frozen[i]) continue;
    capSpeed(players[i] as PlayerBallState, config.player.hardSpeedCap);
  }
  goldens.forEach((g, i) => {
    if (!tangible[i]) return;
    capSpeed(g, config.golden.maxSpeed);
  });

  // Safety net (architecture 12.7). Should never trigger; emits a diagnostic if it does.
  const bodies: { id: string; body: Body }[] = [
    ...players.map((p) => ({ id: p.id, body: p as Body })),
    ...goldens.filter((_, i) => tangible[i]).map((g) => ({ id: g.id, body: g as Body })),
  ];
  for (const { id, body } of bodies) {
    if (insideSolid(config, body.x, body.y)) {
      const cell = nearestFreeCell(config, body.x, body.y);
      body.x = cell.col + 0.5;
      body.y = cell.row + 0.5;
      sink.emit({ type: 'SAFETY_DEPENETRATION', entityId: id });
    }
  }

  for (const [key, strength] of impacts) {
    const [a, b] = key.split('|') as [string, string];
    sink.emit({ type: 'IMPACT', a, b, strength });
  }
}
